import {announce} from '@github-ui/aria-live'
import type {BoardPaging} from '@github-ui/board-core'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useRef, useState} from 'react'

import {appendAxisPage} from './append-axis-page'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardAxis,
  SavedViewsBoardAxisPage,
  SavedViewsBoardAxisPageRequest,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
} from './types'

const NO_AXES: ReadonlySet<SavedViewsBoardAxis> = new Set()
const MAX_PINNED_KEYS = 1_000
// The first request, and one retry against a pinned axis that grew while it was out.
const MAX_ATTEMPTS = 2
const PAGE_FAILED = 'Could not load more board groups. Try again.'
const AXIS_LIMIT_REACHED = 'Too many board groups are loaded to continue. Narrow the view and try again.'
const AXIS_FAILED = 'Could not load more board groups.'

type AxisRequest = {
  boardId: string
  requestId: number
  controller: AbortController
  settled: boolean
}

type BoardAxisPaging = Pick<BoardPaging, 'onLoadMoreColumns' | 'onLoadMoreRows'> & {
  loadingAxes: ReadonlySet<SavedViewsBoardAxis>
}

type AxisPageResult<TItem extends SavedViewsBoardItem, TPageMetadata> =
  | {ok: true; page: SavedViewsBoardAxisPage<TItem, TPageMetadata>}
  | {ok: false; error: unknown}

/** What the held board says to do with a page that came back. */
type AxisPageFit<TItem extends SavedViewsBoardItem, TMetadata> =
  | {kind: 'stale'}
  | {kind: 'retry'; pinnedKeys: string[]}
  | {kind: 'append'; payload: SavedViewsBoardPayload<TItem, TMetadata>}

function axisCursor<TItem extends SavedViewsBoardItem>(
  payload: SavedViewsBoardPayload<TItem, unknown>,
  axis: SavedViewsBoardAxis,
): string | null {
  return (axis === 'columns' ? payload.boardData.columnsCursor : payload.boardData.rowsCursor) ?? null
}

function pinnedKeys<TItem extends SavedViewsBoardItem>(
  payload: SavedViewsBoardPayload<TItem, unknown>,
  axis: SavedViewsBoardAxis,
): string[] {
  const pinned = axis === 'columns' ? payload.boardData.data.rows : payload.boardData.data.columns
  return pinned.map(value => value.key)
}

function sameKeys(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((key, index) => key === right[index])
}

/**
 * A 422 (a refused key, a cursor minted for another field, too many item keys) and a 500 (a page
 * that cut a cell it was sized to fit) come back the same on every retry. A 503 or a network
 * failure may not.
 */
function failsEveryTime(error: unknown): boolean {
  return isResponseError(error) && (error.response.status === 422 || error.response.status === 500)
}

async function requestAxisPage<TItem extends SavedViewsBoardItem, TPageMetadata>(
  fetchAxisPage: NonNullable<SavedViewsBoardAdapter<TItem, unknown, TPageMetadata>['fetchAxisPage']>,
  request: SavedViewsBoardAxisPageRequest & SavedViewsBoardOptions,
): Promise<AxisPageResult<TItem, TPageMetadata>> {
  try {
    return {ok: true, page: await fetchAxisPage(request)}
  } catch (error) {
    return {ok: false, error}
  }
}

function fitAxisPage<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>(
  held: SavedViewsBoardPayload<TItem, TMetadata> | undefined,
  {
    cursor,
    requestedPinnedKeys,
    page,
    appendMetadata,
  }: {
    cursor: string
    requestedPinnedKeys: readonly string[]
    page: SavedViewsBoardAxisPage<TItem, TPageMetadata>
    appendMetadata: SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>['appendAxisMetadata']
  },
): AxisPageFit<TItem, TMetadata> {
  if (!held || axisCursor(held, page.axis) !== cursor) return {kind: 'stale'}

  const heldPinnedKeys = pinnedKeys(held, page.axis)
  if (!sameKeys(heldPinnedKeys, requestedPinnedKeys)) return {kind: 'retry', pinnedKeys: heldPinnedKeys}

  return {kind: 'append', payload: appendAxisPage(held, page, appendMetadata)}
}

function stopAxis<TItem extends SavedViewsBoardItem, TMetadata>(
  held: SavedViewsBoardPayload<TItem, TMetadata>,
  axis: SavedViewsBoardAxis,
): SavedViewsBoardPayload<TItem, TMetadata> {
  const {boardData} = held
  const {data} = boardData

  return {
    ...held,
    boardData:
      axis === 'columns'
        ? {...boardData, columnsCursor: null, data: {...data, hasMoreColumns: false, columnsTruncated: true}}
        : {...boardData, rowsCursor: null, data: {...data, hasMoreRows: false, rowsTruncated: true}},
  }
}

export function useBoardAxisPaging<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>({
  adapter,
  options,
  queryKey,
}: {
  adapter: SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>
  options: SavedViewsBoardOptions
  queryKey: QueryKey
}): BoardAxisPaging {
  const queryClient = useQueryClient()
  const [loadingAxes, setLoadingAxes] = useState<ReadonlySet<SavedViewsBoardAxis>>(NO_AXES)
  const requestsRef = useRef(new Map<SavedViewsBoardAxis, AxisRequest>())
  const lastRequestIdRef = useRef(0)
  const boardId = hashKey(queryKey)
  const {appendAxisMetadata, fetchAxisPage} = adapter
  const {query} = options

  useEffect(() => {
    const outstanding = requestsRef.current
    return () => {
      for (const request of outstanding.values()) request.controller.abort()
      outstanding.clear()
      setLoadingAxes(NO_AXES)
    }
  }, [boardId])

  const onLoadMore = useCallback(
    (axis: SavedViewsBoardAxis, visiblePinnedKeys: readonly string[] | undefined) => {
      if (!fetchAxisPage || requestsRef.current.has(axis)) return

      const held = queryClient.getQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey)
      if (!held) return
      const cursor = axisCursor(held, axis)
      if (cursor === null) return

      const stopPaging = (message: string) => {
        const current = queryClient.getQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey)
        if (!current || axisCursor(current, axis) !== cursor) return

        queryClient.setQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey, stopAxis(current, axis))
        announce(message)
      }

      const initialPinnedKeys = pinnedKeys(held, axis)
      if (initialPinnedKeys.length > MAX_PINNED_KEYS) {
        stopPaging(AXIS_LIMIT_REACHED)
        return
      }

      const requestId = (lastRequestIdRef.current += 1)
      const controller = new AbortController()
      const request: AxisRequest = {boardId, requestId, controller, settled: false}
      const owns = () => {
        const current = requestsRef.current.get(axis)
        return current?.boardId === boardId && current.requestId === requestId
      }
      const finish = () => {
        if (!owns()) return
        request.settled = true
        // Keep both directions blocked until an overlapping pair and its reconciliation retry settle.
        if ([...requestsRef.current.values()].some(current => !current.settled)) return

        requestsRef.current.clear()
        setLoadingAxes(NO_AXES)
      }

      requestsRef.current.set(axis, request)
      setLoadingAxes(previous => new Set(previous).add(axis))

      const load = async () => {
        let requestedPinnedKeys = initialPinnedKeys
        let failed = false

        // No `throw` in here: React Compiler cannot lower one inside `try`. The catch is for an
        // unexpected exception, so the loading barrier still comes down.
        try {
          for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
            const result = await requestAxisPage(fetchAxisPage, {
              axis,
              cursor,
              pinnedKeys: requestedPinnedKeys,
              // Unchanged on the retry. Adding every lane the other axis just brought would split the
              // card budget across lanes that may not be drawn.
              visiblePinnedKeys,
              query,
              signal: controller.signal,
            })
            // An abort clears the record too, so this also covers a board that was left.
            if (!owns()) return

            if (!result.ok && failsEveryTime(result.error)) {
              stopPaging(AXIS_FAILED)
              break
            }
            if (!result.ok || result.page.axis !== axis) {
              failed = true
              break
            }

            const fit = fitAxisPage(queryClient.getQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey), {
              cursor,
              requestedPinnedKeys,
              page: result.page,
              appendMetadata: appendAxisMetadata,
            })
            if (fit.kind === 'append') {
              queryClient.setQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey, fit.payload)
            }
            if (fit.kind !== 'retry') break

            if (fit.pinnedKeys.length > MAX_PINNED_KEYS) {
              stopPaging(AXIS_LIMIT_REACHED)
              break
            }
            // The pinned axis grew again while the retry was out.
            if (attempt === MAX_ATTEMPTS) {
              failed = true
              break
            }
            requestedPinnedKeys = fit.pinnedKeys
          }
        } catch {
          failed = true
        }

        if (failed && !controller.signal.aborted) announce(PAGE_FAILED)
        finish()
      }

      void load()
    },
    [appendAxisMetadata, boardId, fetchAxisPage, query, queryClient, queryKey],
  )

  const onLoadMoreColumns = useCallback(
    (visibleRowKeys?: readonly string[]) => onLoadMore('columns', visibleRowKeys),
    [onLoadMore],
  )
  const onLoadMoreRows = useCallback(
    (visibleColumnKeys?: readonly string[]) => onLoadMore('rows', visibleColumnKeys),
    [onLoadMore],
  )

  return {
    onLoadMoreColumns: fetchAxisPage ? onLoadMoreColumns : undefined,
    onLoadMoreRows: fetchAxisPage ? onLoadMoreRows : undefined,
    loadingAxes,
  }
}

export function withLoadingAxes<TItem extends SavedViewsBoardItem>(
  boardData: SavedViewsBoardData<TItem>,
  loadingAxes: ReadonlySet<SavedViewsBoardAxis>,
): SavedViewsBoardData<TItem> {
  if (loadingAxes.size === 0) return boardData

  return {
    ...boardData,
    data: {
      ...boardData.data,
      isLoadingColumns: loadingAxes.has('columns'),
      isLoadingRows: loadingAxes.has('rows'),
    },
  }
}
